import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../../contexts/AppContext';

const ProjectEvaluationExecution = () => {
  const { projectId, subjectId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useApp();

  const [subject, setSubject] = useState(null);
  const [students, setStudents] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [skills, setSkills] = useState([]);
  const [evaluations, setEvaluations] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEvaluationData();
  }, [projectId, subjectId]);

  const loadEvaluationData = async () => {
    try {
      setLoading(true);

      // Mock data - aquí se haría la llamada real a la API
      const mockSubject = {
        id: subjectId,
        name: 'Matemáticas',
        teacherName: 'Profesor Demo'
      };

      // Mock OAs configurados para la asignatura
      const mockObjectives = [
        { id: 'oa1', code: 'OA1', name: 'Comprender conceptos básicos', type: 'proceso' },
        { id: 'oa2', code: 'OA2', name: 'Resolver problemas', type: 'proceso' },
        { id: 'oa3', code: 'OA3', name: 'Analizar patrones', type: 'producto' },
        { id: 'oa4', code: 'OA4', name: 'Representar información', type: 'producto' },
      ];

      // Mock habilidades seleccionadas para la asignatura
      const mockSkills = [
        { id: 'comprender', name: 'Comprender', level: 2 },
        { id: 'aplicar', name: 'Aplicar', level: 3 },
        { id: 'analizar', name: 'Analizar', level: 4 },
      ];

      // Mock estudiantes del proyecto
      const mockStudents = [
        { id: 1, firstName: 'Juan', lastName: 'González' },
        { id: 2, firstName: 'María', lastName: 'Fernández' },
        { id: 3, firstName: 'Pedro', lastName: 'Martínez' },
        { id: 4, firstName: 'Ana', lastName: 'López' },
        { id: 5, firstName: 'Carlos', lastName: 'Rodríguez' },
        { id: 6, firstName: 'Laura', lastName: 'Sánchez' },
        { id: 7, firstName: 'Diego', lastName: 'Torres' },
        { id: 8, firstName: 'Sofía', lastName: 'Ramírez' },
      ];

      // Ordenar estudiantes alfabéticamente por apellido
      const sortedStudents = mockStudents.sort((a, b) =>
        a.lastName.localeCompare(b.lastName)
      );

      setSubject(mockSubject);
      setObjectives(mockObjectives);
      setSkills(mockSkills);
      setStudents(sortedStudents);

      // Inicializar evaluaciones vacías
      const initialEvaluations = {};
      sortedStudents.forEach(student => {
        mockObjectives.forEach(objective => {
          initialEvaluations[`${student.id}-${objective.id}`] = '';
        });
      });
      setEvaluations(initialEvaluations);

    } catch (error) {
      showError('Error al cargar los datos de evaluación');
    } finally {
      setLoading(false);
    }
  };

  const handleEvaluationChange = (studentId, objectiveId, value) => {
    // Validar que el valor esté entre 1 y 5 o vacío
    if (value !== '' && (isNaN(value) || value < 1 || value > 5)) {
      return;
    }

    const key = `${studentId}-${objectiveId}`;
    setEvaluations({
      ...evaluations,
      [key]: value
    });
  };

  const getScoreColor = (score) => {
    if (!score || score === '') return 'bg-white';

    const numScore = parseInt(score);
    if (numScore === 5) return 'bg-green-100';
    if (numScore === 3 || numScore === 4) return 'bg-yellow-100';
    if (numScore === 1 || numScore === 2) return 'bg-red-100';

    return 'bg-white';
  };

  const calculateStudentTotal = (studentId, type = null) => {
    let total = 0;
    const filteredObjectives = type
      ? objectives.filter(obj => obj.type === type)
      : objectives;

    filteredObjectives.forEach(objective => {
      const key = `${studentId}-${objective.id}`;
      const score = evaluations[key];
      if (score !== '' && !isNaN(score)) {
        total += parseInt(score);
      }
    });
    return total;
  };

  const handleSaveEvaluations = () => {
    // Aquí se guardaría en la API
    showSuccess('Evaluaciones guardadas correctamente');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(`/projects/${projectId}`)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Volver al proyecto
          </button>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Ejecución de Evaluación
            </h1>
            <p className="text-gray-600">
              Asignatura: <span className="font-semibold">{subject?.name}</span> • Profesor: {subject?.teacherName}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Ingresa las calificaciones de 1 a 5 para cada estudiante en cada objetivo de aprendizaje
            </p>
          </div>
        </div>

        {/* Habilidades seleccionadas */}
        {skills.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Habilidades seleccionadas:</h3>
            <div className="flex flex-wrap gap-3">
              {skills.map((skill) => (
                <div
                  key={skill.id}
                  className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-50 border border-purple-200 rounded-lg"
                >
                  <div className="flex items-center justify-center w-6 h-6 bg-purple-600 text-white text-xs font-bold rounded-full">
                    {skill.level}
                  </div>
                  <span className="text-sm font-medium text-purple-900">{skill.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabla de Evaluación */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                {/* Fila de agrupación Proceso/Producto */}
                <tr>
                  <th
                    scope="col"
                    rowSpan={2}
                    className="sticky left-0 z-10 bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200"
                    style={{ minWidth: '200px' }}
                  >
                    Estudiante
                  </th>
                  {objectives.filter(obj => obj.type === 'proceso').length > 0 && (
                    <th
                      scope="col"
                      colSpan={objectives.filter(obj => obj.type === 'proceso').length}
                      className="px-4 py-2 text-center text-xs font-semibold text-orange-700 uppercase tracking-wider bg-orange-50 border-x border-gray-300"
                    >
                      Proceso
                    </th>
                  )}
                  <th
                    scope="col"
                    rowSpan={2}
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-orange-100 border-l border-gray-300"
                    style={{ minWidth: '100px' }}
                  >
                    Total<br/>Proceso
                  </th>
                  {objectives.filter(obj => obj.type === 'producto').length > 0 && (
                    <th
                      scope="col"
                      colSpan={objectives.filter(obj => obj.type === 'producto').length}
                      className="px-4 py-2 text-center text-xs font-semibold text-green-700 uppercase tracking-wider bg-green-50 border-x border-gray-300"
                    >
                      Producto
                    </th>
                  )}
                  <th
                    scope="col"
                    rowSpan={2}
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-green-100 border-l border-gray-300"
                    style={{ minWidth: '100px' }}
                  >
                    Total<br/>Producto
                  </th>
                </tr>
                {/* Fila de OAs individuales */}
                <tr>
                  {objectives.filter(obj => obj.type === 'proceso').map((objective) => (
                    <th
                      key={objective.id}
                      scope="col"
                      className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-orange-50 border-x border-gray-200"
                      style={{ minWidth: '120px' }}
                    >
                      <div className="flex flex-col items-center">
                        <span className="font-bold">{objective.code}</span>
                        <span className="text-xs font-normal text-gray-400 mt-1 line-clamp-2">
                          {objective.name}
                        </span>
                      </div>
                    </th>
                  ))}
                  {objectives.filter(obj => obj.type === 'producto').map((objective) => (
                    <th
                      key={objective.id}
                      scope="col"
                      className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-green-50 border-x border-gray-200"
                      style={{ minWidth: '120px' }}
                    >
                      <div className="flex flex-col items-center">
                        <span className="font-bold">{objective.code}</span>
                        <span className="text-xs font-normal text-gray-400 mt-1 line-clamp-2">
                          {objective.name}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {students.map((student, index) => {
                  const totalProceso = calculateStudentTotal(student.id, 'proceso');
                  const totalProducto = calculateStudentTotal(student.id, 'producto');
                  return (
                    <tr key={student.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="sticky left-0 z-10 px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200 bg-inherit">
                        {student.lastName}, {student.firstName}
                      </td>
                      {/* Inputs de Proceso */}
                      {objectives.filter(obj => obj.type === 'proceso').map((objective) => {
                        const key = `${student.id}-${objective.id}`;
                        const score = evaluations[key];
                        return (
                          <td key={objective.id} className="px-4 py-4 whitespace-nowrap text-center bg-orange-50/30">
                            <input
                              type="number"
                              min="1"
                              max="5"
                              value={score}
                              onChange={(e) => handleEvaluationChange(student.id, objective.id, e.target.value)}
                              className={`w-16 px-3 py-2 text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${getScoreColor(score)}`}
                              placeholder="-"
                            />
                          </td>
                        );
                      })}
                      {/* Total Proceso */}
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-bold text-orange-900 bg-orange-100 border-l border-gray-300">
                        {totalProceso}
                      </td>
                      {/* Inputs de Producto */}
                      {objectives.filter(obj => obj.type === 'producto').map((objective) => {
                        const key = `${student.id}-${objective.id}`;
                        const score = evaluations[key];
                        return (
                          <td key={objective.id} className="px-4 py-4 whitespace-nowrap text-center bg-green-50/30">
                            <input
                              type="number"
                              min="1"
                              max="5"
                              value={score}
                              onChange={(e) => handleEvaluationChange(student.id, objective.id, e.target.value)}
                              className={`w-16 px-3 py-2 text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${getScoreColor(score)}`}
                              placeholder="-"
                            />
                          </td>
                        );
                      })}
                      {/* Total Producto */}
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-bold text-green-900 bg-green-100 border-l border-gray-300">
                        {totalProducto}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Botones de acción */}
        <div className="mt-6 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            <span className="font-medium">{students.length}</span> estudiantes •
            <span className="font-medium ml-1">{objectives.length}</span> objetivos de aprendizaje
          </div>
          <div className="flex space-x-4">
            <button
              onClick={() => navigate(`/projects/${projectId}`)}
              className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSaveEvaluations}
              className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Guardar Evaluaciones
            </button>
          </div>
        </div>

        {/* Leyenda de colores */}
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Escala de Evaluación:</h3>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-green-100 border border-gray-300 rounded"></div>
              <span className="text-sm text-gray-600">Excelente (5)</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-yellow-100 border border-gray-300 rounded"></div>
              <span className="text-sm text-gray-600">Bueno/Satisfactorio (3-4)</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-red-100 border border-gray-300 rounded"></div>
              <span className="text-sm text-gray-600">Insuficiente (1-2)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectEvaluationExecution;
